// V4.23 — controlled behavioral learning: repeated interest becomes a
// SUGGESTION the user must confirm — never silently saved memory.
import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Brain, Check, X } from "lucide-react";
import { toast } from "sonner";
import { confirmMemorySuggestion, dismissMemorySuggestion } from "../../api/aiApi";

export const MemorySuggestionChip = ({ suggestion }) => {
  const { t } = useTranslation("ai");
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["ai-greeting"] });
  const confirmMut = useMutation({
    mutationFn: () => confirmMemorySuggestion(suggestion.key, suggestion.value),
    onSuccess: () => { toast.success(t("memory.suggestionSaved")); invalidate(); },
    onError: () => toast.error(t("assistant.genericError")),
  });
  const dismissMut = useMutation({
    mutationFn: () => dismissMemorySuggestion(suggestion.key, suggestion.value),
    onSuccess: invalidate,
  });
  if (!suggestion) return null;
  return (
    <div
      data-testid="ai-memory-suggestion-chip"
      className="mx-auto flex max-w-md items-center gap-2.5 rounded-2xl border border-sky-500/30 bg-sky-500/5 px-4 py-2.5"
    >
      <Brain className="h-4 w-4 shrink-0 text-sky-300" />
      <span className="min-w-0 flex-1 text-left text-xs leading-relaxed text-sky-200/90">
        {t("memory.suggestionText", {
          label: t(`memory.keys.${suggestion.key}`, { defaultValue: suggestion.label }),
          value: suggestion.value,
        })}
      </span>
      <button
        type="button"
        data-testid="ai-memory-suggestion-confirm"
        disabled={confirmMut.isPending}
        onClick={() => confirmMut.mutate()}
        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 transition hover:bg-emerald-500/20"
      >
        <Check className="h-3 w-3" /> {t("memory.suggestionYes")}
      </button>
      <button
        type="button"
        data-testid="ai-memory-suggestion-dismiss"
        disabled={dismissMut.isPending}
        onClick={() => dismissMut.mutate()}
        className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[11px] text-zinc-500 transition hover:text-zinc-300"
      >
        <X className="h-3 w-3" /> {t("memory.suggestionNo")}
      </button>
    </div>
  );
};
